import { isSameDay } from 'date-fns';

import { getDaysFromLanguage } from '@rover/components/src/datetime/DayOfWeekPicker/DayOfWeekPicker';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import Boarding from '@rover/icons/svg/boarding.svg';
import Daycare from '@rover/icons/svg/daycare.svg';
import DropIn from '@rover/icons/svg/drop-in.svg';
import Grooming from '@rover/icons/svg/grooming.svg';
import Sitting from '@rover/icons/svg/sitting.svg';
import Walking from '@rover/icons/svg/walking.svg';
import { SERVICE_SLUG_PRIORITY } from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/SitterProfilePage';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { PetType } from '@rover/types/src/Pet';

import { formatDateRange } from '../../../../search/SearchPage/components/StickyFiltersBar/utils';

const isServiceType = (slug: string): slug is ServiceType =>
  (Object.values(ServiceType) as string[]).includes(slug);

const getSlugPriorityIndex = (slug: string): number => {
  if (!isServiceType(slug)) return SERVICE_SLUG_PRIORITY.length;
  const index = SERVICE_SLUG_PRIORITY.indexOf(slug);
  return index === -1 ? SERVICE_SLUG_PRIORITY.length : index;
};

const dedupeBySlug = (
  services: EnhancedPersonSitterProfileService[]
): EnhancedPersonSitterProfileService[] => {
  const seenSlugs = new Set<string>();
  return services.filter((service) => {
    if (seenSlugs.has(service.slug)) return false;
    seenSlugs.add(service.slug);
    return true;
  });
};

export const getUniqueServicesSortedByPriority = (
  services: EnhancedPersonSitterProfileService[]
): EnhancedPersonSitterProfileService[] => {
  const uniqueServices = dedupeBySlug(services);
  return [...uniqueServices].sort(
    (a, b) => getSlugPriorityIndex(a.slug) - getSlugPriorityIndex(b.slug)
  );
};

export const getSitterSlugFromWebUrl = (webUrl: string): string => {
  try {
    const { pathname } = new URL(webUrl, 'http://x');
    return pathname.split('/').filter(Boolean)[1] ?? '';
  } catch {
    return '';
  }
};

export const buildContactUrl = (contactUrl: string, locationSearch: string): string => {
  const searchParams = locationSearch.startsWith('?')
    ? locationSearch.substring(1)
    : locationSearch;
  if (!searchParams) return contactUrl;
  const separator = contactUrl.includes('?') ? '&' : '?';
  return `${contactUrl}${separator}${searchParams}`;
};

const AVAILABILITY_SECTION_SELECTOR = '[data-testid="sitter-availability"]';

export const scrollToAvailabilitySection = (): void => {
  if (!process.env.JS_ENV_CLIENT) return;
  document
    .querySelector(AVAILABILITY_SECTION_SELECTOR)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const getPetCountLabel = (petType: PetType, count: number): string => {
  switch (petType) {
    case PetType.CAT:
      return count === 1 ? `${count} cat` : `${count} cats`;
    case PetType.PUPPY:
      return count === 1 ? `${count} puppy` : `${count} puppies`;
    default:
      return count === 1 ? `${count} dog` : `${count} dogs`;
  }
};

/**
 * The end date only counts as a range when it lands on a later calendar day: the
 * profile falls back to "now" when the URL has no `end_date`, which is the same
 * day as a start date of today but a few hours ahead of its midnight timestamp.
 */
export const getRangeEndDate = (selectedDate: Date, selectedEndDate?: Date): Date | undefined => {
  if (!selectedEndDate) return undefined;
  if (isSameDay(selectedDate, selectedEndDate) || selectedEndDate <= selectedDate) return undefined;
  return selectedEndDate;
};

/**
 * The weekdays a repetition covers, in the order the reader's locale lists them — the
 * same names the day of week picker labels its buttons with.
 */
export const formatWeekDays = (locale: Locale, weekDays: number[]): string =>
  getDaysFromLanguage(locale)
    .filter(({ dayId }) => weekDays.includes(dayId))
    .map(({ shortName }) => shortName)
    .join(', ');

export const formatSelectedDates = (
  locale: Locale,
  selectedDate: Date,
  selectedEndDate?: Date
): string => formatDateRange(selectedDate, getRangeEndDate(selectedDate, selectedEndDate), locale);

export const serviceIconToGraphic: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  'sitter-home': Boarding,
  'sitter-traveling': Sitting,
  homevists: DropIn,
  daycare: Daycare,
  walking: Walking,
  grooming: Grooming,
};
